import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Loader2, TrendingUp, MousePointer2 } from "lucide-react";

// Tries to truncate long bilingual headers like "Velocidade de corte real (M/m)\nReal Cutting speed (M/m)"
function shortLabel(col) {
  if (!col) return "";
  const first = String(col).split("\n")[0].trim();
  return first.length > 32 ? first.slice(0, 32) + "…" : first;
}

const COLORS = [
  "#10b981", // emerald
  "#0ea5e9", // sky
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
];

function toFloat(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return v;
  const s = String(v).trim().replace(",", ".");
  if (!s) return null;
  if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);
  // hh:mm:ss -> seconds
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    const p = s.split(":").map(Number);
    if (p.length === 2) return p[0] * 60 + p[1];
    return p[0] * 3600 + p[1] * 60 + p[2];
  }
  return null;
}

// Detects which columns are numeric across the given rows (>=50% numeric)
function detectNumericCols(columns, rows) {
  const out = [];
  columns.forEach((col, ci) => {
    let total = 0;
    let nums = 0;
    rows.forEach((r) => {
      if (ci >= r.length) return;
      const v = r[ci];
      if (v === null || v === undefined || v === "") return;
      total += 1;
      if (toFloat(v) !== null) nums += 1;
    });
    if (total > 0 && nums / total >= 0.5) out.push(col);
  });
  return out;
}

// Extracts a label for the X axis from a cell value (typically Start/End time)
// Examples: "07:06:24\n07:13:14" -> "07:06"; "07:06:24" -> "07:06"
function extractRowTime(v, fallback) {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  const m = s.match(/(\d{1,2}):(\d{2})(:\d{2})?/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return fallback;
}

export default function CapturesEvolutionChart({ run }) {
  const [selectedCols, setSelectedCols] = useState([]);

  // Reset selection when run changes
  useEffect(() => {
    setSelectedCols([]);
  }, [run?.id]);

  if (!run) {
    return (
      <div className="p-6 text-sm text-muted-foreground flex items-start gap-3" data-testid="captures-evolution-empty">
        <MousePointer2 className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          Selecciona uma captura na tab <strong>Tabela</strong> à esquerda para veres o gráfico da evolução das suas linhas (cortes/marcas).
        </div>
      </div>
    );
  }

  const columns = run.columns || [];
  const rows = run.rows || [];

  // Find numeric columns in this run
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const numericCols = useMemo(() => detectNumericCols(columns, rows), [columns, rows]);

  // Auto-select up to 3 numeric columns when run changes
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (selectedCols.length === 0 && numericCols.length > 0) {
      setSelectedCols(numericCols.slice(0, 3));
    }
  }, [numericCols, selectedCols.length]);

  // Build chart data: each row becomes a point. X axis = row time (parsed from
  // start/end column if present) or just the row index.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const chartData = useMemo(() => {
    // Try to find a time-like column to use as x-axis label
    const timeColIdx = columns.findIndex((c) => /inicio|fim|start|end|hora|time/i.test(c));
    return rows.map((row, idx) => {
      const pt = { x: timeColIdx >= 0 ? extractRowTime(row[timeColIdx], `#${idx + 1}`) : `#${idx + 1}`, _idx: idx + 1 };
      selectedCols.forEach((col) => {
        const ci = columns.indexOf(col);
        if (ci >= 0) {
          const v = toFloat(row[ci]);
          if (v !== null) pt[col] = v;
        }
      });
      return pt;
    });
  }, [columns, rows, selectedCols]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const hasAnyData = useMemo(() => chartData.some((p) => selectedCols.some((c) => typeof p[c] === "number")), [chartData, selectedCols]);

  function toggleCol(col) {
    setSelectedCols((prev) => {
      if (prev.includes(col)) return prev.filter((c) => c !== col);
      if (prev.length >= 5) return prev;
      return [...prev, col];
    });
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground flex items-start gap-3">
        <TrendingUp className="h-4 w-4 mt-0.5 shrink-0" />
        <div>Esta captura não tem linhas para representar graficamente.</div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3" data-testid="captures-evolution-chart">
      {/* Header info */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Captura seleccionada:</span>
        <span>{run.started_at ? new Date(run.started_at).toLocaleString("pt-PT") : run.id?.slice(0, 8)}</span>
        <span className="text-muted-foreground/60">·</span>
        <span>{rows.length} linha{rows.length === 1 ? "" : "s"} × {columns.length} colunas</span>
      </div>

      {/* Column toggles */}
      {numericCols.length === 0 ? (
        <div className="text-xs text-muted-foreground p-3 border border-dashed border-border rounded-lg">
          Esta captura não tem colunas numéricas para representar graficamente.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          <div className="text-[11px] text-muted-foreground self-center mr-1">Colunas:</div>
          {numericCols.map((col, i) => {
            const active = selectedCols.includes(col);
            const colorIdx = selectedCols.indexOf(col);
            const color = colorIdx >= 0 ? COLORS[colorIdx % COLORS.length] : undefined;
            return (
              <button
                key={col}
                type="button"
                onClick={() => toggleCol(col)}
                title={col}
                data-testid={`evo-col-toggle-${i}`}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors max-w-[220px] truncate ${
                  active ? "border-transparent text-white" : "border-border text-muted-foreground hover:bg-muted"
                }`}
                style={active ? { backgroundColor: color } : undefined}
              >
                {shortLabel(col)}
              </button>
            );
          })}
        </div>
      )}

      {/* Chart */}
      {numericCols.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-2">
          {!hasAnyData ? (
            <div className="p-6 text-sm text-muted-foreground">Selecciona pelo menos uma coluna numérica acima.</div>
          ) : (
            <div className="h-[320px] sm:h-[380px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="x" stroke="hsl(var(--muted-foreground))" fontSize={10} tickMargin={6} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickMargin={6} width={40} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                    formatter={(value, name) => [Number(value).toFixed(2), shortLabel(name)]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value) => <span style={{ color: "hsl(var(--foreground))" }}>{shortLabel(value)}</span>}
                  />
                  {selectedCols.map((col, idx) => (
                    <Line
                      key={col}
                      type="monotone"
                      dataKey={col}
                      name={col}
                      stroke={COLORS[idx % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
